import {
  BottomSheet,
  Divider,
  Illustration,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { ProgressCarouselBottomSheet } from "@src/appV2/redesign/components/ProgressCarouselBottomSheet";
import { uniqueId } from "lodash";
import { type ReactNode, useState } from "react";

import { getSafeAreaPaddings } from "../../../utils/getSafeAreaPaddings";

interface RestrictionsBottomSheetProps {
  readonly modalState: UseModalState;
  readonly nextRestrictionDurationInDays: number;
}

function KeepScoreAboveZeroSlide() {
  return (
    <>
      <Illustration type="circled-cross-with-exclamation" />
      <Text variant="h3">Keep your score above zero!</Text>
      <Text>A score of zero or below results in an account Restriction.</Text>
    </>
  );
}

function RestrictedImplicationsSlide({
  nextRestrictionDurationInDays,
}: {
  readonly nextRestrictionDurationInDays: number;
}) {
  return (
    <>
      <Text variant="h3">When Restricted, your account:</Text>
      <Stack spacing={5} padding={5} width="100%" textAlign="left">
        <Text>
          Is <b>removed</b> from previous booked shifts.
        </Text>
        <Text>
          Is <b>unable to book</b> new open shifts.
        </Text>
        <Text>
          Is still able to receive and accept <b>Shift Invites</b>.
        </Text>
        <Divider color="dark" />
        <Stack direction="row" spacing={5} justifyContent="space-between" alignItems="center">
          <Text variant="h6">Lifts after</Text>
          <Text variant="body1">{nextRestrictionDurationInDays} days</Text>
        </Stack>
      </Stack>
    </>
  );
}

function UnlimitedScoreSlide() {
  return (
    <>
      <Illustration type="scalloped-check-with-star" />
      <Text variant="h3">Your score has no limit!</Text>
      <Text>
        Build yourself a buffer for a rainy day by showing up on time and committing to shifts.
      </Text>
    </>
  );
}

interface SlideProps {
  readonly content: ReactNode;
}

function Slide(props: SlideProps) {
  const { content } = props;

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      textAlign="center"
      padding={7}
      spacing={5}
      overflow="auto"
      flex={1}
      minHeight={0} // Ensure this can shrink
      height="100%"
      display="flex"
      flexDirection="column"
    >
      {content}
    </Stack>
  );
}

export function RestrictionsBottomSheet(props: RestrictionsBottomSheetProps) {
  const { modalState, nextRestrictionDurationInDays } = props;

  const [step, setStep] = useState(0);

  const steps = [
    <KeepScoreAboveZeroSlide key="keep-score-above-zero-slide" />,
    <RestrictedImplicationsSlide
      key="restricted-implications-slide"
      nextRestrictionDurationInDays={nextRestrictionDurationInDays}
    />,
    <UnlimitedScoreSlide key="unlimited-score-slide" />,
  ];

  const closeBottomSheet = () => {
    modalState.closeModal();
    setStep(0);
  };

  const getBackgroundGradient = (): string => {
    // Green gradient
    if (step === 2) {
      // eslint-disable-next-line cbh-rules/no-inline-gradient -- TODO: migrate to theme palette gradient
      return "linear-gradient(180deg, rgba(250, 248, 247, 0.00) 0%, #FFF 72.7%), linear-gradient(117deg, #FBF2E8 18.83%, #DAF1E8 96.06%)";
    }

    // Red gradient
    // eslint-disable-next-line cbh-rules/no-inline-gradient -- TODO: migrate to theme palette gradient
    return "linear-gradient(353deg, #FFF 37.36%, #FFFAF5 74.27%, #F7D0D0 111.62%)";
  };

  return (
    <BottomSheet
      modalState={modalState}
      paperSx={{ ...getSafeAreaPaddings({ bottom: true }), background: getBackgroundGradient() }}
    >
      <ProgressCarouselBottomSheet
        currentIndex={step}
        closeBottomSheet={closeBottomSheet}
        onIndexChange={setStep}
      >
        {steps.map((step) => (
          <Slide key={uniqueId("carousel-")} content={step} />
        ))}
      </ProgressCarouselBottomSheet>
    </BottomSheet>
  );
}
